import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "skillName",
    "category",
    "cohortType",
    "context",
    "progressAlert",
    "progressBar",
    "progressPercent",
    "progressTitle",
    "progressMessage",
    "generateBtn",
    "cancelBtn"
  ];

  async handleGenerate() {
    // Validate inputs
    const skillName = this.skillNameTarget.value.trim();
    const categoryId = this.categoryTarget.value;
    const cohortType = this.cohortTypeTarget.value;
    const context = this.contextTarget.value.trim();

    if (!skillName) {
      alert("Please enter a skill name");
      this.skillNameTarget.focus();
      return;
    }

    if (!categoryId) {
      alert("Please select a category");
      this.categoryTarget.focus();
      return;
    }

    if (!cohortType) {
      alert("Please select a program type");
      this.cohortTypeTarget.focus();
      return;
    }

    // Disable generate button and show progress
    this.generateBtnTarget.disabled = true;
    this.cancelBtnTarget.disabled = true;
    this.progressAlertTarget.style.display = "block";

    try {
      // Step 1: Create the skill
      this.updateProgress(10, "Creating skill record...");
      const createResponse = await fetch("/skills", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          skill: {
            name: skillName,
            skill_category_id: categoryId,
            cohort_type: cohortType,
            active: true,
          },
        }),
      });

      if (!createResponse.ok) {
        throw new Error("Failed to create skill");
      }

      const skillData = await createResponse.json();
      const skillId = skillData.id;

      // Step 2: Call AI generation endpoint
      this.updateProgress(30, "Calling AI to generate checklist...");
      
      const generateResponse = await fetch(`/skills/${skillId}/generate_from_prompt`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          prompt: skillName,
          context: context || null,
        }),
      });

      if (!generateResponse.ok) {
        throw new Error("Failed to start AI generation");
      }

      // Step 3: Poll for completion
      this.updateProgress(50, "AI is generating checklist items...");
      await this.pollForCompletion(skillId);

    } catch (error) {
      console.error("Generation error:", error);
      alert(`Failed to generate skill: ${error.message}`);
      
      // Re-enable buttons
      this.generateBtnTarget.disabled = false;
      this.cancelBtnTarget.disabled = false;
      this.progressAlertTarget.style.display = "none";
    }
  }

  async pollForCompletion(skillId) {
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes max (5 second intervals)
    let lastProgress = 50;

    return new Promise((resolve, reject) => {
      const pollingInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await fetch(`/skills/${skillId}/upload_status`);
          const data = await response.json();

          // Update progress based on status
          let progress = 50;
          let message = "";

          if (attempts < 4) {
            // First 15 seconds
            progress = 50 + attempts * 5;
            message = "AI is analyzing the skill...";
          } else if (data.has_description) {
            progress = 70;
            message = "Description generated...";
          } else if (data.has_checklist_items > 0) {
            progress = 75 + Math.min(data.has_checklist_items, 20);
            message = `Generated ${data.has_checklist_items} checklist items...`;
          } else {
            // Still processing, increment slowly
            progress = Math.min(lastProgress + 1, 90);
            message = `AI working (${attempts * 5}s elapsed)...`;
          }

          lastProgress = progress;
          this.updateProgress(progress, message);

          if (data.processing_complete) {
            clearInterval(pollingInterval);
            this.updateProgress(100, "Complete! Redirecting...");

            setTimeout(() => {
              window.location.href = `/skills/${skillId}`;
            }, 800);

            resolve(data);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollingInterval);
            this.updateProgress(90, "Processing is taking longer than expected...");

            // Show option to view skill anyway after timeout
            if (confirm("Processing is taking longer than expected. Would you like to view the skill now?")) {
              window.location.href = `/skills/${skillId}`;
            } else {
              // Re-enable buttons
              this.generateBtnTarget.disabled = false;
              this.cancelBtnTarget.disabled = false;
              this.progressAlertTarget.style.display = "none";
            }

            reject(new Error("Processing timeout"));
          }
        } catch (error) {
          console.error("Polling error:", error);
          // Continue polling even if one request fails
        }
      }, 5000); // Poll every 5 seconds
    });
  }

  updateProgress(percent, message) {
    this.progressBarTarget.style.width = `${percent}%`;
    this.progressPercentTarget.textContent = `${percent}%`;
    this.progressMessageTarget.textContent = message;

    // Update title based on progress
    if (percent < 30) {
      this.progressTitleTarget.textContent = "Setting up...";
    } else if (percent < 70) {
      this.progressTitleTarget.textContent = "AI is Generating...";
    } else if (percent < 100) {
      this.progressTitleTarget.textContent = "Finalizing...";
    } else {
      this.progressTitleTarget.textContent = "Complete!";
    }
  }
}

